/* ============================================================
   FERAL 2026 — Cart drawer interactions
   ------------------------------------------------------------
   Goal: cart feels instant, no race conditions, no glitches.

   How it works:
   1. ELF already debounces the change event 300ms before calling
      updateQuantity, so rapid +/- clicks coalesce into one call.
   2. Optimistic UI: we run BEFORE ELF (capture phase) and update
      line price / subtotal / checkout / remaining / progress fill
      synchronously, capped by the input's max attribute (which is
      now the lesser of quantity_rule.max and inventory_quantity).
   3. Single-flight: we patch CartItems.prototype.updateQuantity
      ONLY (CartDrawerItems extends CartItems and inherits — no
      double-wrap). If a request is already in flight, the new args
      are stored as queued and fired the moment the previous one
      completes via patched disableLoading.
   4. ELF's loading-overlay class triggers a small inline spinner
      next to the line price (not a full row dim).
   ============================================================ */
(function () {
  'use strict';

  /* ── Clear stale "There was an error while updating your cart" ──────────
     Dawn's cart.js sets #CartDrawer-CartErrors text on update failures but
     never clears it. Once set, the message lingers in the empty-state UI
     for the rest of the session. We:
       1. Clear on page load
       2. Clear on every drawer class change (open/close/empty/active)
       3. Watch the error element ITSELF for text being written, and
          immediately clear it if the cart is empty (no real error to show
          on an empty cart — it's always a stale leftover). */
  function clearCartError() {
    var ids = ['CartDrawer-CartErrors', 'cart-errors'];
    for (var i = 0; i < ids.length; i++) {
      var el = document.getElementById(ids[i]);
      if (el) el.textContent = '';
    }
  }
  clearCartError(); // baseline on page load

  /* ── Scroll items list to top every time the drawer opens ──
     Dawn's cart-drawer.js adds the `active` class when the drawer opens
     (via open() and via renderContents). On every open we want the user
     to see the top of the cart — which, combined with the reversed
     cart.items loop in cart-drawer.liquid, means the newly-added item.
     Without this, browsers preserve the previous scroll position so a
     returning visit to the drawer can land mid-list. */
  function scrollItemsToTop() {
    var body = document.querySelector('cart-drawer .feral-cart__body');
    if (body) body.scrollTop = 0;
  }

  var cartDrawerEl = document.querySelector('cart-drawer');
  if (cartDrawerEl && typeof MutationObserver !== 'undefined') {
    var wasActive = cartDrawerEl.classList.contains('active');
    new MutationObserver(function () {
      clearCartError();
      var isActive = cartDrawerEl.classList.contains('active');
      // Fire on every false→true transition (every open, not just first).
      if (isActive && !wasActive) {
        // requestAnimationFrame so the scroll happens after the open
        // animation has started laying out — otherwise WebKit can
        // sometimes ignore scrollTop set during a layout transition.
        requestAnimationFrame(scrollItemsToTop);
        // The body has no layout height until the drawer is visible, so
        // (re)compute the always-visible scrollbar now that it's open.
        scheduleScrollbar();
      }
      wasActive = isActive;
    }).observe(cartDrawerEl, { attributes: true, attributeFilter: ['class'] });
    // Watch the error element itself — Dawn writes here on failures; if
    // the cart is currently empty, instantly clear (the message is always
    // stale on an empty cart, since there's nothing to have failed).
    var errEl = document.getElementById('CartDrawer-CartErrors');
    if (errEl) {
      new MutationObserver(function () {
        if (cartDrawerEl.classList.contains('is-empty') && errEl.textContent.trim()) {
          errEl.textContent = '';
        }
      }).observe(errEl, { childList: true, characterData: true, subtree: true });
    }
  }

  /* ── Money formatter (mirrors Shopify's money / money_with_currency) ── */
  var SYMBOLS = { GBP: '£', EUR: '€', USD: '$', AUD: 'A$', CAD: 'CA$', NZD: 'NZ$' };
  var DECIMAL_CACHE = {};
  function decimalsFor(currency) {
    if (DECIMAL_CACHE[currency] != null) return DECIMAL_CACHE[currency];
    var d = 2;
    try { d = new Intl.NumberFormat(undefined, { style: 'currency', currency: currency }).resolvedOptions().maximumFractionDigits; } catch (e) {}
    DECIMAL_CACHE[currency] = d;
    return d;
  }
  function format(cents, currency, withCode) {
    var sym = SYMBOLS[currency] || (currency + ' ');
    var amount = Math.max(0, Math.round(cents)) / 100;
    return sym + amount.toFixed(decimalsFor(currency)) + (withCode ? ' ' + currency : '');
  }

  /* ── Number flip-in animation (re-trigger by class swap + reflow) ── */
  function flip(el) {
    if (!el) return;
    el.classList.remove('feral-flip-in');
    el.offsetWidth; // eslint-disable-line no-unused-expressions
    el.classList.add('feral-flip-in');
  }

  /* ── Element finders ── */
  function $progress()  { return document.querySelector('[data-feral-progress]'); }
  function $fill()      { return document.querySelector('[data-feral-progress-fill]'); }
  function $total()     { return document.querySelector('[data-feral-money-total]'); }
  function $remaining() { return document.querySelector('[data-feral-money-remaining]'); }

  /* Next-day is only sold while the order can go out on the next dispatch: Mon-Fri before
     12:00 London, or from 21:00 the evening before (feral-shipping config/next-day.json,
     whose bank holidays are listed here too). Outside it the bag says Free shipping. */
  var ND_HOLIDAYS = ['2026-12-25', '2026-12-28', '2027-01-01', '2027-03-26', '2027-03-29', '2027-05-03', '2027-05-31', '2027-08-30', '2027-12-27', '2027-12-28'];
  function nextDayOpen() {
    try {
      var parts = {};
      new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' })
        .formatToParts(new Date()).forEach(function (x) { parts[x.type] = x.value; });
      var hour = parseInt(parts.hour, 10);
      var day = new Date(Date.UTC(+parts.year, +parts.month - 1, +parts.day));
      if (hour >= 21) day.setUTCDate(day.getUTCDate() + 1);
      else if (hour >= 12) return false;
      var wd = day.getUTCDay();
      return wd >= 1 && wd <= 5 && ND_HOLIDAYS.indexOf(day.toISOString().slice(0, 10)) === -1;
    } catch (e) { return false; }
  }
  function syncNextDay() {
    var progress = $progress();
    if (!progress || !progress.dataset.ndCents || !progress.classList.contains('has-nd') || nextDayOpen()) return;
    // Closed: back to the one-goal bar, its fill measured to the free-delivery gate.
    progress.classList.remove('has-nd');
    var fill = $fill(), threshold = parseInt(progress.dataset.thresholdCents || '0', 10);
    if (fill && threshold) {
      var pct = Math.min(100, Math.round(parseInt(progress.dataset.eligibleCents || '0', 10) * 100 / threshold));
      fill.dataset.targetPct = String(pct); fill.style.width = pct + '%';
    }
  }

  function syncUnlocked() {
    syncNextDay();
    var fill = $fill(), progress = $progress();
    if (!fill || !progress) return;
    if (progress.classList.contains('has-nd')) {
      // The fill runs to £100 here, so the gates are read off the money, not the fill.
      var eligible = parseInt(progress.dataset.eligibleCents || '0', 10);
      progress.classList.toggle('is-unlocked', eligible >= parseInt(progress.dataset.thresholdCents || '0', 10));
      progress.classList.toggle('is-nd', eligible >= parseInt(progress.dataset.ndCents || '0', 10));
      return;
    }
    progress.classList.toggle('is-unlocked', parseFloat(fill.dataset.targetPct || '0') >= 100);
  }
  syncUnlocked();

  /* -- Multi-currency free-ship threshold (International local currencies + NOK) --
     The 4 single-presentment markets (GBP/EUR/USD/AUD) hardcode their exact gate in the
     Liquid case. Every other presentment currency falls to the else branch, where the real
     gate is the 8500-pence (GBP 85) International delivery rate. Liquid cannot FX-convert,
     so it tags the bar with data-gate-gbp="8500" and hides it; here we convert GBP 85 to
     presentment via Shopify.currency.rate so the bar matches the real gate for EVERY currency
     (cart values are uniformly x100, incl. zero-decimal JPY/KRW, so one formula works).
     Fail-safe: no reliable rate -> leave the bar hidden, never a false "free unlocked". */
  function fmtMoneyCC(cents, currency) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency, maximumFractionDigits: 0 }).format(Math.max(0, cents) / 100);
    } catch (e) {
      return (SYMBOLS[currency] || (currency + ' ')) + Math.round(Math.max(0, cents) / 100);
    }
  }
  function initDynamicThreshold() {
    var p = $progress();
    if (!p) return;
    var gateGbp = parseInt(p.getAttribute('data-gate-gbp') || '0', 10);
    if (!gateGbp) return; // only the multi-currency else case carries this
    var rate = (window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate)) || 0;
    if (!rate || rate <= 0) return; // no reliable FX -> leave bar hidden, never a false promise
    var currency = p.dataset.currency || 'GBP';
    var threshold = Math.round(gateGbp * rate);
    p.dataset.thresholdCents = String(threshold);
    var totalEl = $total();
    var subtotal = parseInt(p.dataset.eligibleCents || '0', 10);
    if (p.dataset.neonOnly === 'true') subtotal = threshold;
    var remaining = Math.max(0, threshold - subtotal);
    var pct = subtotal >= threshold ? 100 : (threshold > 0 ? Math.round(subtotal * 100 / threshold) : 0);
    var remEl = $remaining();
    if (remEl) { remEl.dataset.cents = String(remaining); remEl.textContent = fmtMoneyCC(remaining, currency); }
    var tlEl = p.querySelector('[data-feral-threshold-label]');
    if (tlEl) tlEl.textContent = fmtMoneyCC(threshold, currency);
    var fill = $fill();
    if (fill) { fill.dataset.targetPct = String(pct); fill.style.width = pct + '%'; }
    p.classList.toggle('is-unlocked', subtotal >= threshold);
    /* 🚨 WAS `p.style.visibility = ''`, PAIRED WITH AN INLINE `visibility:hidden`
       IN THE LIQUID. That was safe while [data-feral-progress] wrapped nothing
       but a bar; the same element now carries the bag title AND the close
       button, so hiding it until FX resolved would hide the only control that
       shuts the drawer. The pending state is a class that hides the two
       FX-dependent children and nothing else. */
    p.classList.remove('is-fx-pending');
  }
  initDynamicThreshold();

  /* ── Always-visible custom red scrollbar for the items list ──────────
     The native ::-webkit-scrollbar auto-hides (and iOS Safari never renders
     a persistent/red one), so shoppers couldn't tell the list scrolls past
     the fold — items below looked like they weren't there. Build a persistent
     red indicator in the static #CartDrawer-Form (survives Dawn's
     renderContents), overlaying the right edge of .feral-cart__body and
     tracking scroll position. Pure visual (pointer-events:none) — never
     intercepts touch scrolling. Shows only when the list actually overflows. */
  var cartScrollbarUpdate = function () {};
  function initCartScrollbar() {
    var body = document.querySelector('cart-drawer .feral-cart__body');
    if (!body) return;
    var form = body.parentElement;
    if (!form) return;
    var rail = form.querySelector('.feral-cart__scrollbar');
    if (!rail) {
      rail = document.createElement('div');
      rail.className = 'feral-cart__scrollbar';
      rail.setAttribute('aria-hidden', 'true');
      var newThumb = document.createElement('div');
      newThumb.className = 'feral-cart__scrollbar-thumb';
      rail.appendChild(newThumb);
      form.appendChild(rail);
    }
    var thumb = rail.querySelector('.feral-cart__scrollbar-thumb');
    function update() {
      var sh = body.scrollHeight, ch = body.clientHeight;
      if (!ch || sh <= ch + 1) {
        rail.classList.remove('is-scrollable');
        body.classList.remove('has-more');
        return;
      }
      rail.classList.add('is-scrollable');
      /* The bottom fade is about "is there anything under the fold", which is
         not the same question as "does this list scroll" — at the very bottom
         of a long list there is nothing left to hint at, and a fade there just
         dims the last row for no reason. */
      body.classList.toggle('has-more', body.scrollTop + ch < sh - 8);
      var trackH = rail.clientHeight || ch;
      var thumbH = Math.max(24, Math.round(trackH * ch / sh));
      var maxScroll = sh - ch;
      var y = maxScroll > 0 ? Math.round((body.scrollTop / maxScroll) * (trackH - thumbH)) : 0;
      thumb.style.height = thumbH + 'px';
      thumb.style.transform = 'translateY(' + y + 'px)';
    }
    cartScrollbarUpdate = update;
    if (!body._feralSbBound) {
      body._feralSbBound = true;
      body.addEventListener('scroll', function () { requestAnimationFrame(update); }, { passive: true });
    }
    update();
  }
  var _sbRaf = 0;
  function scheduleScrollbar() {
    if (_sbRaf) return;
    _sbRaf = requestAnimationFrame(function () { _sbRaf = 0; initCartScrollbar(); });
  }
  initCartScrollbar();
  window.addEventListener('resize', scheduleScrollbar, { passive: true });

  /* Dawn's renderContents() replaces the drawer body with fresh Liquid on every
     cart mutation, and the fresh else-branch markup arrives hidden with the
     999999999 placeholder (Liquid can't FX-convert). Without a re-run, any
     multi-currency shopper loses the bar the moment they add to cart. The
     placeholder guard makes re-entry after conversion a no-op, so the observer
     can't loop on its own mutations. */
  if (cartDrawerEl && typeof MutationObserver !== 'undefined') {
    new MutationObserver(function () {
      // Items added/removed change the scroll height — refresh the indicator.
      scheduleScrollbar();
      var p = $progress();
      if (p && p.getAttribute('data-gate-gbp') && p.dataset.thresholdCents === '999999999') {
        initDynamicThreshold();
      }
    }).observe(cartDrawerEl, { childList: true, subtree: true });
  }

  /* ── Optimistic UI on +/- click (capture phase, before ELF) ──
     ELF's stepUp already respects input.max. We mirror that check
     so the optimistic display never shows a value the input itself
     would refuse — preventing the "shows 6 then snaps to 5 with
     error" feeling. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.feral-cart__qty-btn');
    if (!btn) return;

    var qtyInput = btn.parentElement && btn.parentElement.querySelector('.feral-cart__qty-input');
    if (!qtyInput) return;

    var oldQty = parseInt(qtyInput.value, 10) || 0;
    var newQty = oldQty + (btn.name === 'plus' ? 1 : -1);
    if (newQty < 0) return;

    /* ── Decrement-to-zero → REMOVE the line ──
       Dawn's cart.js validateQuantity rejects any new value below
       data-min (= quantity_rule.min, default 1) and silently resets
       the input back to its previous value. That meant clicking minus
       at qty=1 looked like "spinning forever" — our optimistic UI
       locked the row but Shopify never received the change, so a
       refresh showed the item still there. Pivot to the Remove path:
       cancel this click and trigger the row's remove button instead,
       which calls updateQuantity(line, 0) and Shopify treats that as
       a delete. */
    if (btn.name === 'minus' && newQty === 0) {
      e.preventDefault();
      e.stopImmediatePropagation();
      var thisRow = btn.closest('.feral-cart__item');
      var removeBtn = thisRow && thisRow.querySelector('.feral-cart__remove, cart-remove-button button');
      if (removeBtn) removeBtn.click();
      return;
    }

    // Hard cap at input's max (= min(quantity_rule.max, inventory_quantity))
    if (qtyInput.max && newQty > parseInt(qtyInput.max, 10)) return;

    var row = btn.closest('.feral-cart__item');
    var priceEl = row && row.querySelector('.feral-cart__price');
    if (!row || !priceEl || !priceEl.dataset.cents) return;

    // LOCK the row's qty pill immediately — pointer-events: none + dim + spinner.
    // Release happens on disableLoading (or naturally when ELF replaces the HTML).
    row.classList.add('feral-cart__item--syncing');

    var currency = priceEl.dataset.currency || 'GBP';
    var oldLineCents = parseInt(priceEl.dataset.cents, 10);
    var unitCents = oldQty > 0 ? Math.round(oldLineCents / oldQty) : 0;
    var newLineCents = unitCents * newQty;
    var delta = newLineCents - oldLineCents;

    // Line price
    priceEl.dataset.cents = newLineCents.toString();
    if (newQty > 0) {
      priceEl.textContent = format(newLineCents, currency, false);
      flip(priceEl);
    }

    // Subtotal
    var totalEl = $total();
    if (totalEl && totalEl.dataset.cents) {
      var newTotal = parseInt(totalEl.dataset.cents, 10) + delta;
      totalEl.dataset.cents = newTotal.toString();
      totalEl.textContent = format(newTotal, currency, true);
      flip(totalEl);
    }

    // (Checkout button no longer shows the amount — "Secure checkout" + padlock)

    // Remaining + progress fill
    var progress = $progress();
    if (progress && totalEl && totalEl.dataset.cents) {
      var thresholdCents = parseInt(progress.dataset.thresholdCents || '0', 10);
      var shippingDelta = (parseInt(row.dataset.shippingUnit || '0', 10)) * (newQty - oldQty);
      var subtotal = Math.max(0, parseInt(progress.dataset.eligibleCents || '0', 10) + shippingDelta);
      progress.dataset.eligibleCents = String(subtotal);
      if (progress.dataset.neonOnly === 'true') subtotal = thresholdCents;
      var newRemaining = Math.max(0, thresholdCents - subtotal);

      var remainingEl = $remaining();
      if (remainingEl) {
        remainingEl.dataset.cents = newRemaining.toString();
        remainingEl.textContent = format(newRemaining, currency, false);
        flip(remainingEl);
      }

      var ndGate = progress.classList.contains('has-nd') ? parseInt(progress.dataset.ndCents || '0', 10) : 0;
      var newPct = ndGate ? Math.min(100, subtotal * 100 / ndGate)
        : (subtotal >= thresholdCents ? 100 : (subtotal * 100 / thresholdCents));
      var fill = $fill();
      if (fill) {
        fill.dataset.targetPct = newPct.toString();
        fill.style.width = newPct + '%';
      }
      progress.classList.toggle('is-unlocked', subtotal >= thresholdCents);
      // UK free next-day from £100 (Harry, 28 Oct 2026); the Liquid re-render confirms it.
      var ndCents = parseInt(progress.dataset.ndCents || '0', 10);
      if (ndCents) {
        var ndLeft = Math.max(0, ndCents - subtotal);
        var ndEl = progress.querySelector('[data-feral-nd-remaining]');
        if (ndEl) ndEl.textContent = format(ndLeft, currency, false);
        progress.classList.toggle('is-nd', ndLeft === 0);
      }
    }
  }, true); // capture so we run before ELF's quantity-input change handler

  /* ── Lock the row when "Remove" is clicked too — same UX as +/- ── */
  document.addEventListener('click', function (e) {
    var rm = e.target.closest('.feral-cart__remove');
    if (!rm) return;
    var row = rm.closest('.feral-cart__item');
    if (row) row.classList.add('feral-cart__item--syncing');
  }, true);

  /* ── Sync unlocked-state class after ELF re-renders the cart drawer HTML ── */
  var inner = document.querySelector('#CartDrawer');
  if (inner) {
    new MutationObserver(syncUnlocked).observe(inner, { childList: true, subtree: true });
  }

  /* ── Single-flight enforcement on ELF's updateQuantity ──
     CartDrawerItems extends CartItems and inherits updateQuantity —
     so we ONLY patch CartItems. Patching both would double-wrap and
     deadlock (the inner wrapper sees the outer's in-flight=true and
     queues forever). */
  var Cls = customElements.get('cart-items');
  if (Cls && Cls.prototype && typeof Cls.prototype.updateQuantity === 'function' && !Cls.prototype._feralPatched) {
    Cls.prototype._feralPatched = true;

    var origUpdate = Cls.prototype.updateQuantity;
    var origDisable = Cls.prototype.disableLoading;

    Cls.prototype.updateQuantity = function (line, quantity, eventName, variantId) {
      var self = this;
      // If a previous request is in flight, store latest args. Older
      // queued args are overwritten — we only ever care about the LAST one.
      if (self._feralInFlight) {
        self._feralQueuedArgs = [line, quantity, eventName, variantId];
        return;
      }
      self._feralInFlight = true;
      origUpdate.call(self, line, quantity, eventName, variantId);
    };

    Cls.prototype.disableLoading = function (line) {
      origDisable.call(this, line);
      var self = this;
      self._feralInFlight = false;

      // If we have a queued request, leave the row LOCKED — another AJAX
      // is about to fire. Only release the lock if the cart is fully settled.
      if (self._feralQueuedArgs) {
        var args = self._feralQueuedArgs;
        self._feralQueuedArgs = null;
        setTimeout(function () {
          if (self._feralInFlight) return;
          self._feralInFlight = true;
          origUpdate.apply(self, args);
        }, 16);
      } else {
        // No more pending — release any lingering syncing locks.
        // (HTML replacement usually destroys the locked row, but if for
        // any reason the row survived, this guarantees it unlocks.)
        document.querySelectorAll('.feral-cart__item--syncing').forEach(function (r) {
          r.classList.remove('feral-cart__item--syncing');
        });
      }
    };
  }

  /* ── Impulse add-ons rail ("last call.") ─────────────────────────
     Cards are rendered by Liquid in cart-drawer.liquid from the
     cart-add-ons collection. All handlers are delegated on document so
     they survive Dawn's renderContents HTML replacement. The add POSTs
     /cart/add.js with `sections` in the body (one round trip) and swaps
     the fresh drawer + cart-icon markup — the same replacement Dawn
     itself performs, so every observer above (scrollbar, threshold,
     unlocked-state) re-fires as normal. Single-flight: one add at a
     time; buttons show an inline spinner meanwhile. */
  var upsellInFlight = false;

  function upsellSwapSections(sections) {
    if (sections && sections['cart-drawer']) {
      var doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      var fresh = doc.querySelector('#CartDrawer');
      var target = document.querySelector('#CartDrawer');
      if (fresh && target) target.innerHTML = fresh.innerHTML;
    }
    if (sections && sections['cart-icon-bubble']) {
      var doc2 = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      var ss = doc2.querySelector('.shopify-section');
      var host = document.getElementById('cart-icon-bubble');
      if (ss && host) host.innerHTML = ss.innerHTML;
    }
  }

  document.addEventListener('click', function (e) {
    // Open the in-card size picker (multi-variant products)
    var pick = e.target.closest('[data-feral-upsell-pick]');
    if (pick) {
      var pickCard = pick.closest('[data-feral-upsell-card]');
      if (pickCard) {
        pickCard.classList.add('is-picking');
        pick.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    // Close the picker
    var cancel = e.target.closest('[data-feral-upsell-cancel]');
    if (cancel) {
      var cancelCard = cancel.closest('[data-feral-upsell-card]');
      if (cancelCard) {
        cancelCard.classList.remove('is-picking');
        var pickBtn = cancelCard.querySelector('[data-feral-upsell-pick]');
        if (pickBtn) pickBtn.setAttribute('aria-expanded', 'false');
      }
      return;
    }

    // Add to bag (one-tap button or a size pill)
    var btn = e.target.closest('[data-feral-upsell-add]');
    if (!btn) return;
    if (upsellInFlight || btn.classList.contains('is-adding')) return;
    var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
    if (!variantId) return;

    upsellInFlight = true;
    btn.classList.add('is-adding');
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: root
      })
    })
      .then(function (r) {
        return r.json().then(function (data) { return { ok: r.ok, data: data }; });
      })
      .then(function (res) {
        // Shopify signals add failures (e.g. just sold out) with a `status` field.
        if (!res.ok || res.data.status) {
          throw new Error((res.data && (res.data.description || res.data.message)) || 'add failed');
        }
        upsellSwapSections(res.data.sections);
        // The fresh markup puts the new item at the top of the list —
        // scroll back up so the shopper sees it land in the bag.
        var body = document.querySelector('cart-drawer .feral-cart__body');
        if (body) body.scrollTop = 0;
      })
      .catch(function () {
        // Leave the card usable again and flash the button red briefly.
        btn.classList.remove('is-adding');
        btn.classList.add('is-error');
        setTimeout(function () { btn.classList.remove('is-error'); }, 1600);
      })
      .then(function () { upsellInFlight = false; });
  });

  /* ── Rail scroll chevrons (desktop) ──
     Mice have no horizontal swipe, so the rail header carries prev/next
     buttons. Shown (via .is-scrollable on the rail root) only when the
     track overflows; each arrow disables at its end stop. Scroll events
     don't bubble but DO capture, so one document-level capture listener
     tracks the state across every Dawn re-render without re-binding. */
  function refreshUpsellArrows() {
    var track = document.querySelector('.feral-cart__upsell-track');
    if (!track) return;
    var upsell = track.closest('.feral-cart__upsell');
    if (!upsell) return;
    var overflow = track.scrollWidth > track.clientWidth + 2;
    upsell.classList.toggle('is-scrollable', overflow);
    if (!overflow) return;
    var prev = upsell.querySelector('[data-feral-upsell-prev]');
    var next = upsell.querySelector('[data-feral-upsell-next]');
    var maxLeft = track.scrollWidth - track.clientWidth;
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= maxLeft - 2;
  }

  document.addEventListener('click', function (e) {
    var arrow = e.target.closest('[data-feral-upsell-prev], [data-feral-upsell-next]');
    if (!arrow) return;
    var track = document.querySelector('.feral-cart__upsell-track');
    if (!track) return;
    var card = track.querySelector('.feral-cart__upsell-card');
    if (!card) return;
    var styles = getComputedStyle(track);
    var step = card.getBoundingClientRect().width + (parseFloat(styles.columnGap || styles.gap) || 10);
    var dir = arrow.hasAttribute('data-feral-upsell-next') ? 1 : -1;
    // Index-based target so the destination is always a snap point.
    var idx = Math.round(track.scrollLeft / step) + dir;
    var maxLeft = track.scrollWidth - track.clientWidth;
    var target = Math.max(0, Math.min(idx * step, maxLeft));
    track.scrollTo({ left: target, behavior: 'smooth' });
  });

  var _upsellArrowRaf = 0;
  function scheduleUpsellArrows() {
    if (_upsellArrowRaf) return;
    _upsellArrowRaf = requestAnimationFrame(function () { _upsellArrowRaf = 0; refreshUpsellArrows(); });
  }
  document.addEventListener('scroll', function (e) {
    if (e.target && e.target.classList && e.target.classList.contains('feral-cart__upsell-track')) scheduleUpsellArrows();
  }, true);
  window.addEventListener('resize', scheduleUpsellArrows, { passive: true });
  if (cartDrawerEl && typeof MutationObserver !== 'undefined') {
    // Re-renders replace the rail markup (arrows arrive with prev disabled,
    // scrollLeft reset to 0) — recompute visibility/state after each one.
    new MutationObserver(scheduleUpsellArrows).observe(cartDrawerEl, { childList: true, subtree: true });
  }
  scheduleUpsellArrows();

  /* ══════════════════════════════════════════════════════════════════════
     THE DISCOUNT CODE, AS A DISCLOSURE

     The input used to hold 60px of a phone screen open permanently. It is now
     one tap behind "Add a code" — and the tap must also FOCUS the field, or
     the disclosure has just made the shopper do two things where they used to
     do one. Delegated on document because Dawn's renderContents() replaces
     this entire subtree on every cart mutation.
     ══════════════════════════════════════════════════════════════════════ */
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-feral-code-toggle]');
    if (!t) return;
    var footer = t.closest('.feral-cart__footer');
    var field = footer && footer.querySelector('[data-feral-code]');
    if (!field) return;
    var open = field.getAttribute('data-open') === 'true';
    field.setAttribute('data-open', open ? 'false' : 'true');
    t.setAttribute('aria-expanded', open ? 'false' : 'true');
    t.textContent = open ? 'Add a code' : 'Hide code';
    if (!open) {
      var input = field.querySelector('.feral-cart__discount-input');
      if (input) input.focus();
    }
  });

  /* ══════════════════════════════════════════════════════════════════════
     SWIPE THE SHEET DOWN TO DISMISS

     The grab handle is a promise, and an affordance that does nothing is worse
     than none. Bound on the HEADER only, deliberately — a blanket touch handler
     on the panel would fight the item list's own scroll, and the header is the
     one part of the sheet with nothing scrollable under it.

     ⚠️ DOWNWARD DRAG ONLY, AND ONLY PAST A THRESHOLD. A drag that ends short of
     it springs back rather than closing: a cart that closes on a stray thumb
     movement has thrown away the shopper's basket, and that is not a mistake
     they will make twice with us.
     ⚠️ MOBILE ONLY. Desktop is a side panel, not a sheet; matchMedia is read at
     touch-start rather than at load so a resize cannot leave the handler armed
     on a layout that has no handle.
     ══════════════════════════════════════════════════════════════════════ */
  (function bindSheetDrag() {
    if (!cartDrawerEl) return;
    var panel = cartDrawerEl.querySelector('.feral-cart__panel');
    if (!panel) return;

    var startY = 0, dy = 0, dragging = false, t0 = 0;
    var CLOSE_AT = 90;         // px of travel that counts as "put it away"
    var CLOSE_VELOCITY = 0.5;  // px/ms — a fast flick closes from anywhere

    cartDrawerEl.addEventListener('touchstart', function (e) {
      if (!window.matchMedia('(max-width: 600px)').matches) return;
      if (!e.target.closest || !e.target.closest('.feral-cart__head')) return;
      // Never hijack the close button — it has its own job.
      if (e.target.closest('.feral-cart__close')) return;
      dragging = true;
      startY = e.touches[0].clientY;
      dy = 0;
      t0 = Date.now();
      cartDrawerEl.classList.add('is-dragging');
    }, { passive: true });

    cartDrawerEl.addEventListener('touchmove', function (e) {
      if (!dragging) return;
      dy = e.touches[0].clientY - startY;
      if (dy < 0) dy = 0;              // up is not a gesture here
      panel.style.transform = 'translateY(' + dy + 'px)';
    }, { passive: true });

    function end() {
      if (!dragging) return;
      dragging = false;
      cartDrawerEl.classList.remove('is-dragging');
      panel.style.transform = '';
      var v = dy / Math.max(1, Date.now() - t0);
      if (dy > CLOSE_AT || v > CLOSE_VELOCITY) {
        if (typeof cartDrawerEl.close === 'function') cartDrawerEl.close();
        else cartDrawerEl.classList.remove('active');
      }
      dy = 0;
    }
    cartDrawerEl.addEventListener('touchend', end, { passive: true });
    cartDrawerEl.addEventListener('touchcancel', end, { passive: true });
  })();

})();
